import ReactBeforeSliderComponent from 'react-before-after-slider-component';
import { useState } from "react";
import { useEffect } from 'react';

/**
 * A React component that displays a before-and-after image slider with accompanying text and additional details.
 *
 * @param {Object} props - The props object.
 * @param {Array<Object>} props.images - An array of image objects to be displayed in the slider. Each object should contain a `firstImage` and `secondImage` with `imageUrl` properties.
 * @param {Object} props.images[].firstImage - The first image in the before-after comparison.
 * @param {string} props.images[].firstImage.imageUrl - The URL of the first image.
 * @param {Object} props.images[].secondImage - The second image in the before-after comparison.
 * @param {string} props.images[].secondImage.imageUrl - The URL of the second image.
 * @param {string | Object} [props.subTitle] - An optional subtitle displayed above the title. Can be a string or a custom React element.
 * @param {string} [props.subTitle.custom] - A custom React element for the subtitle.
 * @param {string | Object} [props.title] - The main title for the section. Can be a string or a custom React element.
 * @param {string} [props.title.custom] - A custom React element for the title.
 * @param {string | Object} [props.description] - A description for the section. Can be a string or a custom React element.
 * @param {string} [props.description.custom] - A custom React element for the description.
 * @param {React.ReactNode} [props.additionalDetails] - Additional content to be displayed below the description, such as a list or other details.
 * 
 * @returns {JSX.Element} The rendered BeforeAfter component.
 */

const BeforeAfter = ({ images, subTitle, title, description, additionalDetails }) => {
    const delimiterIconStyles = {
        width: '46px',
        height: '46px',
        backgroundSize: 'cover',
        borderRadius: 'none',
        backgroundImage: `url('/assets/icon.svg')`,
        boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.2)',
    }
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentPercent, setCurrentPercent] = useState(50);
    const [navVisible, setNavVisible] = useState(false);

    const handleDotClick = (index) => {
        setCurrentSlide(index);
    };

    useEffect(() => {
        // Pre-fetch images
        if (images && images.length > 0) {
            images.forEach(imageSet => {
                const firstImage = new Image();
                firstImage.src = imageSet.firstImage.imageUrl;

                const secondImage = new Image();
                secondImage.src = imageSet.secondImage.imageUrl;
            });
        }
    }, [images]);

    return (
        <>
            <div className="split-container inline-split-mode">
                <div className="split-text">
                    <div className="core-text">
                        {subTitle && <span>{subTitle}</span>}
                        {subTitle?.custom && subTitle.custom}

                        {title && <h1>{title}</h1>}
                        {title?.custom && title.custom}

                        {description && <p>{description}</p>}
                        {description?.custom && description.custom}
                    </div>
                    {additionalDetails && additionalDetails}
                </div>
                <div className="before-after-slider-container">
                    <div className="flyby-before-after">
                        <ReactBeforeSliderComponent
                            firstImage={images[currentSlide].firstImage}
                            secondImage={images[currentSlide].secondImage}
                            width={1000}
                            height={600}
                            delimiterIconStyles={delimiterIconStyles}
                            currentPercentPosition={currentPercent}
                            onChangePercentPosition={
                                (percent) => {
                                    setCurrentPercent(percent);
                                    setNavVisible(false)
                                }
                            }
                        />
                    </div>
                    {
                        images.length > 1 && <div className="navigation-dots">
                            {images.map((_, index) => (
                                <span onMouseEnter={
                                    () => handleDotClick(index)
                                } key={index}
                                    className={`dot ${index === currentSlide ? 'active' : ''}`}
                                    onClick={() => handleDotClick(index)}></span>
                            ))}
                        </div>
                    }
                </div>
            </div>
        </>
    )
}

export default BeforeAfter;
